import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login, selectTenant, clearError } from '../store/slices/authSlice';
import api from '../api';
import { useDomainParams } from '../hooks/useDomainParams';

function ClientAdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedPAAS, setSelectedPAAS] = useState('');
  const [selectedSaaS, setSelectedSaaS] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [paasList, setPaasList] = useState([]);
  const [saasList, setSaasList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, user, tenants } = useSelector((state) => state.auth);
  const { paasId: domainPaasId, saasId: domainSaasId, tenantId: domainTenantId, branding, isLoading: domainLoading, error: domainError } = useDomainParams();

  useEffect(() => {
    const fetchDropdowns = async () => {
      setIsLoadingDropdowns(true);
      try {
        const paasRes = await api.get('/paas/public');
        const paasData = paasRes.data.data || [];
        setPaasList(paasData);

        try {
          const saasRes = await api.get('/saas/public');
          setSaasList(saasRes.data.data || []);
        } catch (err) {
          console.error('Failed to fetch SAAS list:', err);
          setSaasList([]);
        }

        try {
          const clientRes = await api.get('/tenants/public');
          setClientList(clientRes.data.data || []);
        } catch (err) {
          console.error('Failed to fetch client list:', err);
        }
      } catch (err) {
        console.error('Failed to fetch dropdown data:', err);
      } finally {
        setIsLoadingDropdowns(false);
      }
    };
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (tenants.length > 0) {
      const validTenants = tenants.filter(t => t.tenantId);
      setPaasList(prev => prev.length > 0 ? prev : validTenants.filter(t => t.paas?.paasId));
      setSaasList(prev => prev.length > 0 ? prev : validTenants.filter(t => t.saas?.saasId));
      setClientList(prev => prev.length > 0 ? prev : validTenants);
    }
  }, [tenants]);

  useEffect(() => {
    if (domainPaasId && paasList.length > 0) {
      const exists = paasList.some(p => p.paasId === domainPaasId);
      if (exists) {
        setSelectedPAAS(domainPaasId);
      }
    }
  }, [domainPaasId, paasList]);

  useEffect(() => {
    if (domainSaasId && saasList.length > 0) {
      const exists = saasList.some(s => s.saasId === domainSaasId);
      if (exists) {
        setSelectedSaaS(domainSaasId);
      }
    }
  }, [domainSaasId, saasList]);

  useEffect(() => {
    if (domainTenantId && clientList.length > 0) {
      const exists = clientList.some(c => c.tenantId === domainTenantId);
      if (exists) {
        setSelectedClient(domainTenantId);
      }
    }
  }, [domainTenantId, clientList]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'superadmin') {
      navigate('/super-admin/dashboard');
    } else if (isAuthenticated && user?.role === 'paasadmin') {
      navigate(`/paas-admin/${selectedPAAS}`);
    } else if (isAuthenticated && user?.role === 'saasadmin') {
      navigate(`/saas-admin/${selectedSaaS}`);
    }
  }, [isAuthenticated, user, navigate]);

  const selectedPAASData = paasList.find(p => p.paasId === selectedPAAS);
  const primaryColor = branding?.primaryColor || '#7c3aed';
  const logoUrl = branding?.logo;

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());

    const result = await dispatch(login({ email, password }));

    if (login.fulfilled.match(result)) {
      const loggedInUser = result.payload.user;
      const userTenants = result.payload.tenants || [];

      if (loggedInUser?.role === 'superadmin') {
        navigate('/super-admin/dashboard');
      } else if (loggedInUser?.role === 'paasadmin') {
        navigate(`/paas-admin/${selectedPAAS}`);
      } else if (loggedInUser?.role === 'saasadmin') {
        navigate(`/saas-admin/${selectedSaaS}`);
      } else if (loggedInUser?.role === 'clientadmin' && userTenants.length > 0) {
        const clientTenant = userTenants[0];
        await dispatch(selectTenant(clientTenant.tenantId));
        navigate(`/client-admin/${clientTenant.tenantId}`);
      } else if (userTenants.length > 0 && selectedClient) {
        const clientTenant = userTenants.find(t => t.tenantId === selectedClient);
        if (clientTenant) {
          await dispatch(selectTenant(clientTenant.tenantId));
          navigate(`/client-admin/${selectedClient}`);
        }
      }
    }
  };

  if (domainError) {
    return (
      <div className="login-page" style={styles.page}>
        <div className="login-card" style={styles.card}>
          <div style={styles.errorBanner}>
            <span style={styles.errorIcon}>⚠️</span>
            <h2 style={styles.errorTitle}>Invalid Client Domain Configuration</h2>
            <p style={styles.errorText}>{domainError}</p>
            <a href="/" style={styles.homeLink}>← Return to Home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={{ ...styles.page, background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}>
      <div className="login-card" style={styles.card}>
        <div style={styles.logoSection}>
          {logoUrl ? (
            <img src={logoUrl} alt="Platform Logo" style={styles.paasLogo} />
          ) : selectedPAASData?.logo ? (
            <img src={`/api/v1${selectedPAASData.logo}`} alt="Platform Logo" style={styles.paasLogo} />
          ) : (
            <div style={styles.logo}>🏢</div>
          )}
          <h1 style={styles.title}>Client Admin</h1>
          <p style={styles.subtitle}>Organization Administration</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          {isLoadingDropdowns || domainLoading ? (
            <div style={styles.loading}>Loading options...</div>
          ) : (
            <>
              {(paasList.length > 0 || saasList.length > 0 || clientList.length > 0) && (
              <>
                <div className="form-group" style={styles.formGroup}>
                  <label style={styles.label}>Select Platform (PAAS)</label>
                  <select
                    value={selectedPAAS}
                    onChange={(e) => {
                      setSelectedPAAS(e.target.value);
                      setSelectedSaaS('');
                      setSelectedClient('');
                    }}
                    style={styles.select}
                  >
                    <option value="">Select a platform...</option>
                    {paasList.map(paas => (
                      <option key={paas.paasId} value={paas.paasId}>
                        {paas.name || paas.paasId}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={styles.formGroup}>
                  <label style={styles.label}>Select Application (SAAS)</label>
                  <select
                    value={selectedSaaS}
                    onChange={(e) => {
                      setSelectedSaaS(e.target.value);
                      setSelectedClient('');
                    }}
                    style={styles.select}
                    disabled={!selectedPAAS}
                  >
                    <option value="">Select an application...</option>
                    {saasList
                      .filter(s => !selectedPAAS || s.paasId === selectedPAAS)
                      .map(saas => (
                      <option key={saas.saasId} value={saas.saasId}>
                        {saas.name || saas.saasId}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={styles.formGroup}>
                  <label style={styles.label}>Select Organization (Client)</label>
                  <select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    style={styles.select}
                    disabled={!selectedSaaS}
                  >
                    <option value="">Select an organization...</option>
                    {clientList.map(client => (
                      <option key={client.tenantId} value={client.tenantId}>
                        {client.tenantName || client.tenantId}
                      </option>
                    ))}
                  </select>
                </div>
              </>
              )}
            </>
          )}

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@organization.com"
              style={styles.input}
              required
            />
          </div>

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={styles.input}
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{...styles.button, background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`, opacity: isLoading ? 0.6 : 1}}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={styles.footer}>
          <a href="/login" style={styles.link}>← Back to Main Login</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  paasLogo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  loading: {
    textAlign: 'center',
    padding: '20px',
    color: '#6b7280',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s',
    outline: 'none',
  },
  select: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    backgroundColor: 'white',
  },
  button: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
  },
  link: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
  },
  errorBanner: {
    textAlign: 'center',
    padding: '20px',
  },
  errorIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: '8px',
  },
  errorText: {
    color: '#6b7280',
    fontSize: '14px',
    marginBottom: '20px',
  },
  homeLink: {
    color: '#4a90d9',
    textDecoration: 'none',
    fontSize: '14px',
  },
};

export default ClientAdminLogin;